import { useState, useEffect } from 'preact/hooks';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { 
  Settings2, 
  Bot, 
  Shield, 
  Zap, 
  FolderOpen, 
  GitBranch,
  Save,
  RotateCcw,
  AlertTriangle,
  CheckCircle,
  Info
} from 'lucide-preact';
import type { ClaudeCodeSession } from '../../shared/types';

interface ClaudeCodeSettings {
  // Model Configuration
  model: string;
  maxTurns: number;
  maxTokens: number;
  
  // Permission Settings
  permissionMode: 'default' | 'acceptEdits' | 'bypassPermissions' | 'plan';
  allowedTools: string[];
  deniedTools: string[];
  additionalDirectories: string[];
  
  // Environment Variables
  env: Record<string, string>;
  
  // GitHub Integration
  githubToken: string;
  autoSync: boolean;
  
  // Session Settings
  cleanupPeriodDays: number;
  includeCoAuthoredBy: boolean;
  
  // Output Settings
  outputStyle: string;
  verbose: boolean;
  
  // Hooks Configuration
  hooks: Record<string, any>;
  disableAllHooks: boolean;
}

interface SettingsPanelProps {
  session: ClaudeCodeSession | null;
  onSettingsChange: (settings: ClaudeCodeSettings) => Promise<void>;
  onSaveSettings: (settings: ClaudeCodeSettings) => Promise<void>;
}

const DEFAULT_SETTINGS: ClaudeCodeSettings = {
  model: 'claude-3-5-sonnet-20241022',
  maxTurns: 10,
  maxTokens: 4000,
  permissionMode: 'default',
  allowedTools: ['Read', 'Write', 'Edit', 'MultiEdit', 'Bash', 'Grep', 'Glob', 'WebSearch', 'WebFetch', 'NotebookEdit', 'TodoWrite'],
  deniedTools: [],
  additionalDirectories: [],
  env: {},
  githubToken: '',
  autoSync: true,
  cleanupPeriodDays: 30,
  includeCoAuthoredBy: true,
  outputStyle: 'terminal',
  verbose: false,
  hooks: {},
  disableAllHooks: false
};

const AVAILABLE_TOOLS = [
  'Read', 'Write', 'Edit', 'MultiEdit', 'Bash', 'Grep', 'Glob', 
  'WebSearch', 'WebFetch', 'NotebookEdit', 'TodoWrite', 'Task',
  'ExitPlanMode', 'ListMcpResources', 'ReadMcpResource'
];

const AVAILABLE_MODELS = [
  'claude-3-5-sonnet-20241022',
  'claude-3-5-haiku-20241022',
  'claude-3-opus-20240229',
  'claude-3-sonnet-20240229',
  'claude-3-haiku-20240307'
];

const OUTPUT_STYLES = [
  'terminal',
  'explanatory',
  'concise',
  'detailed'
];

export function SettingsPanel({ session, onSettingsChange, onSaveSettings }: SettingsPanelProps) {
  const [settings, setSettings] = useState<ClaudeCodeSettings>(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState<'general' | 'permissions' | 'tools' | 'github' | 'advanced'>('general');

  useEffect(() => {
    // Load settings from session or API
    if (session) {
      // Load session-specific settings
      loadSettings();
    }
  }, [session]);

  const loadSettings = async () => {
    try {
      // In a real implementation, this would fetch from the API
      // For now, we'll use default settings
      setSettings(DEFAULT_SETTINGS);
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const handleSettingChange = (key: keyof ClaudeCodeSettings, value: any) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
    setHasChanges(true);
  };

  const handleToolToggle = (tool: string, listType: 'allowedTools' | 'deniedTools') => {
    const currentList = settings[listType];
    const otherList = listType === 'allowedTools' ? settings.deniedTools : settings.allowedTools;
    
    if (currentList.includes(tool)) {
      // Remove from current list
      handleSettingChange(listType, currentList.filter(t => t !== tool));
    } else {
      // Add to current list and remove from other list
      handleSettingChange(listType, [...currentList, tool]);
      handleSettingChange(
        listType === 'allowedTools' ? 'deniedTools' : 'allowedTools',
        otherList.filter(t => t !== tool)
      );
    }
  };

  const handleSaveSettings = async () => {
    setIsLoading(true);
    try {
      await onSaveSettings(settings);
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to save settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetSettings = () => {
    setSettings(DEFAULT_SETTINGS);
    setHasChanges(true);
  };

  const renderGeneralSettings = () => (
    <div className="space-y-6">
      {/* Model Configuration */}
      <div className="space-y-4">
        <div>
          <Label htmlFor="model">Claude Model</Label>
          <select
            id="model"
            value={settings.model}
            onChange={(e) => handleSettingChange('model', (e.target as HTMLSelectElement).value)}
            className="w-full mt-1 p-2 border rounded-md"
          >
            {AVAILABLE_MODELS.map(model => (
              <option key={model} value={model}>{model}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="maxTurns">Max Turns</Label>
            <Input
              id="maxTurns"
              type="number"
              value={settings.maxTurns}
              onInput={(e) => handleSettingChange('maxTurns', parseInt((e.target as HTMLInputElement).value))}
              min="1"
              max="50"
            />
          </div>
          <div>
            <Label htmlFor="maxTokens">Max Tokens</Label>
            <Input
              id="maxTokens"
              type="number"
              value={settings.maxTokens}
              onInput={(e) => handleSettingChange('maxTokens', parseInt((e.target as HTMLInputElement).value))}
              min="100"
              max="8000"
            />
          </div>
        </div>
      </div>

      <Separator />

      {/* Permission Mode */}
      <div className="space-y-4">
        <div>
          <Label htmlFor="permissionMode">Permission Mode</Label>
          <select
            id="permissionMode"
            value={settings.permissionMode}
            onChange={(e) => handleSettingChange('permissionMode', (e.target as HTMLSelectElement).value)}
            className="w-full mt-1 p-2 border rounded-md"
          >
            <option value="default">Default - Ask for permission</option>
            <option value="acceptEdits">Accept Edits - Auto-accept file changes</option>
            <option value="bypassPermissions">Bypass Permissions - Allow all actions</option>
            <option value="plan">Plan Mode - Planning only, no execution</option>
          </select>
        </div>
      </div>

      <Separator />

      {/* Output Settings */}
      <div className="space-y-4">
        <div>
          <Label htmlFor="outputStyle">Output Style</Label>
          <select
            id="outputStyle"
            value={settings.outputStyle}
            onChange={(e) => handleSettingChange('outputStyle', (e.target as HTMLSelectElement).value)}
            className="w-full mt-1 p-2 border rounded-md"
          >
            {OUTPUT_STYLES.map(style => (
              <option key={style} value={style}>{style}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="verbose"
            checked={settings.verbose}
            onChange={(e) => handleSettingChange('verbose', (e.target as HTMLInputElement).checked)}
            className="rounded"
          />
          <Label htmlFor="verbose">Verbose output (show full bash outputs)</Label>
        </div>

        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="includeCoAuthoredBy"
            checked={settings.includeCoAuthoredBy}
            onChange={(e) => handleSettingChange('includeCoAuthoredBy', (e.target as HTMLInputElement).checked)}
            className="rounded"
          />
          <Label htmlFor="includeCoAuthoredBy">Include "co-authored-by Claude" in commits</Label>
        </div>
      </div>
    </div>
  );

  const renderPermissionsSettings = () => (
    <div className="space-y-6">
      {/* Additional Directories */}
      <div className="space-y-4">
        <div>
          <Label>Additional Directories</Label>
          <p className="text-sm text-muted-foreground mb-2">
            Additional directories Claude can access beyond the working directory
          </p>
          <div className="space-y-2">
            {settings.additionalDirectories.map((dir, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  value={dir}
                  onInput={(e) => {
                    const newDirs = [...settings.additionalDirectories];
                    newDirs[index] = (e.target as HTMLInputElement).value;
                    handleSettingChange('additionalDirectories', newDirs);
                  }}
                  placeholder="/path/to/directory"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newDirs = settings.additionalDirectories.filter((_, i) => i !== index);
                    handleSettingChange('additionalDirectories', newDirs);
                  }}
                >
                  Remove
                </Button>
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                handleSettingChange('additionalDirectories', [...settings.additionalDirectories, '']);
              }}
            >
              Add Directory
            </Button>
          </div>
        </div>
      </div>

      <Separator />

      {/* Environment Variables */}
      <div className="space-y-4">
        <div>
          <Label>Environment Variables</Label>
          <p className="text-sm text-muted-foreground mb-2">
            Environment variables to set for Byte DevOps sessions
          </p>
          <div className="space-y-2">
            {Object.entries(settings.env).map(([key, value], index) => (
              <div key={index} className="flex gap-2">
                <Input
                  value={key}
                  onInput={(e) => {
                    const newEnv = { ...settings.env };
                    delete newEnv[key];
                    newEnv[(e.target as HTMLInputElement).value] = value;
                    handleSettingChange('env', newEnv);
                  }}
                  placeholder="Variable name"
                />
                <Input
                  value={value}
                  onInput={(e) => {
                    const newEnv = { ...settings.env };
                    newEnv[key] = (e.target as HTMLInputElement).value;
                    handleSettingChange('env', newEnv);
                  }}
                  placeholder="Variable value"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newEnv = { ...settings.env };
                    delete newEnv[key];
                    handleSettingChange('env', newEnv);
                  }}
                >
                  Remove
                </Button>
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const newEnv = { ...settings.env, '': '' };
                handleSettingChange('env', newEnv);
              }}
            >
              Add Variable
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderToolsSettings = () => (
    <div className="space-y-6">
      {/* Tool Permissions */}
      <div className="space-y-4">
        <div>
          <Label>Tool Permissions</Label>
          <p className="text-sm text-muted-foreground mb-4">
            Configure which tools Byte DevOps can use
          </p>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium text-green-600 mb-2">Allowed Tools</h4>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {AVAILABLE_TOOLS.map(tool => (
                  <div key={tool} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={`allowed-${tool}`}
                      checked={settings.allowedTools.includes(tool)}
                      onChange={() => handleToolToggle(tool, 'allowedTools')}
                      className="rounded"
                    />
                    <Label htmlFor={`allowed-${tool}`} className="text-sm">{tool}</Label>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <h4 className="font-medium text-red-600 mb-2">Denied Tools</h4>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {AVAILABLE_TOOLS.map(tool => (
                  <div key={tool} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={`denied-${tool}`}
                      checked={settings.deniedTools.includes(tool)}
                      onChange={() => handleToolToggle(tool, 'deniedTools')}
                      className="rounded"
                    />
                    <Label htmlFor={`denied-${tool}`} className="text-sm">{tool}</Label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Separator />

      {/* Hooks Configuration */}
      <div className="space-y-4">
        <div>
          <Label>Hooks Configuration</Label>
          <p className="text-sm text-muted-foreground mb-2">
            Configure custom commands to run before or after tool executions
          </p>
          
          <div className="flex items-center space-x-2 mb-4">
            <input
              type="checkbox"
              id="disableAllHooks"
              checked={settings.disableAllHooks}
              onChange={(e) => handleSettingChange('disableAllHooks', (e.target as HTMLInputElement).checked)}
              className="rounded"
            />
            <Label htmlFor="disableAllHooks">Disable all hooks</Label>
          </div>
          
          {!settings.disableAllHooks && (
            <div className="space-y-2">
              <div className="text-sm text-muted-foreground">
                Hook configuration would be implemented here with a more complex UI
                for managing PreToolUse, PostToolUse, and other hook types.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderGitHubSettings = () => (
    <div className="space-y-6">
      {/* GitHub Token */}
      <div className="space-y-4">
        <div>
          <Label htmlFor="githubToken">GitHub Token</Label>
          <p className="text-sm text-muted-foreground mb-2">
            Personal access token for GitHub integration
          </p>
          <Input
            id="githubToken"
            type="password"
            value={settings.githubToken}
            onInput={(e) => handleSettingChange('githubToken', (e.target as HTMLInputElement).value)}
            placeholder="ghp_xxxxxxxxxxxxxxxxxxxx"
          />
        </div>

        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="autoSync"
            checked={settings.autoSync}
            onChange={(e) => handleSettingChange('autoSync', (e.target as HTMLInputElement).checked)}
            className="rounded"
          />
          <Label htmlFor="autoSync">Automatically sync files to GitHub</Label>
        </div>
      </div>

      <Separator />

      {/* GitHub Integration Status */}
      <div className="space-y-4">
        <div>
          <Label>Integration Status</Label>
          <div className="mt-2 space-y-2">
            <div className="flex items-center gap-2">
              {settings.githubToken ? (
                <CheckCircle className="h-4 w-4 text-green-500" />
              ) : (
                <AlertTriangle className="h-4 w-4 text-yellow-500" />
              )}
              <span className="text-sm">
                {settings.githubToken ? 'GitHub token configured' : 'GitHub token not configured'}
              </span>
            </div>
            
            <div className="flex items-center gap-2">
              <Info className="h-4 w-4 text-blue-500" />
              <span className="text-sm text-muted-foreground">
                Configure a GitHub token to enable repository integration and file syncing
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAdvancedSettings = () => (
    <div className="space-y-6">
      {/* Session Management */}
      <div className="space-y-4">
        <div>
          <Label htmlFor="cleanupPeriodDays">Cleanup Period (Days)</Label>
          <p className="text-sm text-muted-foreground mb-2">
            How long to retain chat transcripts locally
          </p>
          <Input
            id="cleanupPeriodDays"
            type="number"
            value={settings.cleanupPeriodDays}
            onInput={(e) => handleSettingChange('cleanupPeriodDays', parseInt((e.target as HTMLInputElement).value))}
            min="1"
            max="365"
          />
        </div>
      </div>

      <Separator />

      {/* Advanced Configuration */}
      <div className="space-y-4">
        <div>
          <Label>Advanced Configuration</Label>
          <p className="text-sm text-muted-foreground mb-4">
            Additional Byte DevOps settings and configurations
          </p>
          
          <div className="space-y-2">
            <div className="text-sm text-muted-foreground">
              Advanced settings like MCP server configuration, custom system prompts,
              and enterprise policy settings would be configured here.
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const tabs = [
    { id: 'general', label: 'General', icon: Settings2 },
    { id: 'permissions', label: 'Permissions', icon: Shield },
    { id: 'tools', label: 'Tools', icon: Zap },
    { id: 'github', label: 'GitHub', icon: GitBranch },
    { id: 'advanced', label: 'Advanced', icon: Bot }
  ] as const;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Settings</h2>
          <p className="text-sm text-muted-foreground">
            Configure Byte DevOps behavior and preferences
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleResetSettings}
            disabled={isLoading}
            size="sm"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset
          </Button>
          <Button
            onClick={handleSaveSettings}
            disabled={!hasChanges || isLoading}
            size="sm"
          >
            <Save className="h-4 w-4 mr-2" />
            {isLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 bg-muted p-1 rounded-lg">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Settings Content */}
      <Card>
        <CardContent className="p-6">
          {activeTab === 'general' && renderGeneralSettings()}
          {activeTab === 'permissions' && renderPermissionsSettings()}
          {activeTab === 'tools' && renderToolsSettings()}
          {activeTab === 'github' && renderGitHubSettings()}
          {activeTab === 'advanced' && renderAdvancedSettings()}
        </CardContent>
      </Card>

      {/* Changes Indicator */}
      {hasChanges && (
        <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 p-3 rounded-md">
          <AlertTriangle className="h-4 w-4" />
          <span>You have unsaved changes</span>
        </div>
      )}
    </div>
  );
}
